import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { useRole, hasRole } from "@/context/RoleContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, Download } from "lucide-react";

const RACI_STYLES = {
  R: "bg-blue-600 text-white",
  A: "bg-purple-600 text-white",
  C: "bg-amber-500 text-white",
  I: "bg-slate-400 text-white",
};

const CLEAR = "__clear__";

export const TeamRACI = ({ assessmentId }) => {
  const role = useRole();
  const isAdmin = hasRole(role, "admin");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRaci = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/grc/${assessmentId}/raci`);
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    setLoading(true);
    fetchRaci();
  }, [fetchRaci]);

  const setCell = async (pillar, teamId, value) => {
    const v = value === CLEAR ? null : value;
    const prev = data;
    setData((d) => {
      const matrix = { ...d.matrix, [pillar]: { ...(d.matrix[pillar] || {}) } };
      if (v) matrix[pillar][teamId] = v;
      else delete matrix[pillar][teamId];
      return { ...d, matrix };
    });
    try {
      await api.put(`/grc/${assessmentId}/raci`, { pillar, team_id: teamId, value: v });
      toast.success(v ? `${pillar} set to ${v}` : `${pillar} cleared`);
    } catch (e) {
      setData(prev);
      toast.error(e?.response?.data?.detail || "Failed to update RACI");
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 rounded-md" />)}
      </div>
    );
  }

  if (!data || data.teams.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl py-16 text-center text-sm text-slate-500" data-testid="raci-empty-state">
        No teams in the directory yet. Ask an admin to add teams.
      </div>
    );
  }

  const downloadExport = async (kind) => {
    try {
      const token = localStorage.getItem("grc_token");
      const res = await fetch(`${API_BASE}/grc/${assessmentId}/raci/export/${kind}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CX0-Team-RACI.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} export downloaded`);
    } catch (e) {
      toast.error(`Could not generate the ${kind.toUpperCase()} export`);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 space-y-4" data-testid="team-raci">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500">Pillar-by-pillar accountability — score %, RAG and R/A/C/I per team.</p>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => downloadExport("csv")}
            data-testid="raci-export-csv"
          >
            <Download size={14} className="mr-1.5" /> Export CSV
          </Button>
          <Button
            size="sm"
            onClick={() => downloadExport("docx")}
            data-testid="raci-export-docx"
            className="bg-[#008080] hover:bg-[#006666]"
          >
            <Download size={14} className="mr-1.5" /> Export DOCX
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="raci-table">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-2 pr-3 font-semibold text-[#003366] min-w-[180px]">Pillar</th>
              {data.teams.map((t) => (
                <th key={t.team_id} className="py-2 px-2 text-center font-semibold text-[#003366]" data-testid={`raci-team-header-${t.name}`}>
                  <div className="flex flex-col items-center gap-0.5">
                    <span>{t.name}</span>
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-normal text-slate-400">
                      <Users size={9} /> {t.member_count} member{t.member_count === 1 ? "" : "s"}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.pillars.map((p) => (
              <tr key={p.code} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`raci-row-${p.code}`}>
                <td className="py-1.5 pr-3">
                  <span className="font-mono font-semibold text-slate-700 mr-2">{p.code}</span>
                  <span className="text-slate-500">{p.name}</span>
                </td>
                {data.teams.map((t) => {
                  const val = data.matrix?.[p.code]?.[t.team_id];
                  return (
                    <td key={t.team_id} className="py-1.5 px-2 text-center">
                      {isAdmin ? (
                        <Select value={val || CLEAR} onValueChange={(v) => setCell(p.code, t.team_id, v)}>
                          <SelectTrigger
                            className={`h-7 w-12 mx-auto justify-center text-[11px] font-bold border-0 ${
                              val ? RACI_STYLES[val] : "bg-slate-100 text-slate-400"
                            }`}
                            data-testid={`raci-cell-${p.code}-${t.name}`}
                          >
                            <SelectValue placeholder="—">{val || "—"}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={CLEAR}>—</SelectItem>
                            {["R", "A", "C", "I"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span
                          className={`inline-flex items-center justify-center w-7 h-7 rounded text-[11px] font-bold ${
                            val ? RACI_STYLES[val] : "bg-slate-50 text-slate-300"
                          }`}
                          data-testid={`raci-cell-${p.code}-${t.name}`}
                        >
                          {val || "—"}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[10px] text-slate-500 border-t border-slate-100 pt-3" data-testid="raci-legend">
        <span><b className="text-blue-600">R</b> Responsible</span>
        <span><b className="text-purple-600">A</b> Accountable</span>
        <span><b className="text-amber-600">C</b> Consulted</span>
        <span><b className="text-slate-500">I</b> Informed</span>
        {!isAdmin && <span className="ml-auto italic">Read-only — only admins can edit assignments</span>}
      </div>
    </div>
  );
};
